import { AutomationStep } from '~/models/automations-steps/automation-step.model';
import {
    AutomationActionFileType,
    AutomationActionType
} from '../components/Automations/AutomationsEnums';
import { AutomationStepActionEmail } from '../models/automations-steps/automations-step-actions/automation-step-action-email';
import { AutomationStepActionFile } from '../models/automations-steps/automations-step-actions/automation-step-action-file';
import { AutomationStepActionFlow } from '../models/automations-steps/automations-step-actions/automation-step-action-flow';
import { AutomationStepActionTask } from '../models/automations-steps/automations-step-actions/automation-step-action-task';
import { TShortTimezone } from '../utils/timezone.utils';
import { IEmailAttachment, IVendorEmail } from './model.types';
import { AutomationStepActionPipeline } from '~/models/automations-steps/automations-step-actions/automation-step-action-pipeline';

export enum AutomationField {
    Title = 'title',
    StepsCount = 'steps_count',
    LastUsageDate = 'last_usage_date',
    UsageCount = 'usage_count'
}

export interface IAutomationTemplate {
    _id: string;
    [AutomationField.Title]: string;
    is_template: boolean;
    relevant_project_types?: string[];
    workflow_steps: IAutomationStep[];
}

export interface IAutomationPreview {
    _id: string;
    title: string;
    relevant_project_types?: string[];
    steps_count: number;
    last_project_name: string;
    last_usage_date: Date;
    usage_count: number;
}

export interface IAutomationLeadFormRelation {
    is_workflow_activated: boolean;
    public_flow_id: string;
    status: string;
    title: string;
    workflow_id?: string;
}

export interface IAutomationInstance extends IAutomationTemplate {
    applied_date?: string | Date;
    clone_from_template?: string;
    project_date?: string;
    project_end_date?: string;
    project_id?: string;
    project_timezone?: TShortTimezone;
    workspace_id?: string;
}

export interface IAutomationStep {
    _id?: string;
    order: number;
    approval_status: TApprovalStatus;
    has_depended_step?: boolean;
    workflow_step_action: IAutomationStepAction;
    workflow_step_trigger: IAutomationStepTrigger;
    session?: IAutomationStepSession;
}

export interface IAutomationStepActionBase {
    _id?: string;
    type: AutomationActionType;
    _type?: string;
    completed?: boolean;
    completed_at?: string;
    declined_at?: string;
    description?: string;
}

export interface IAutomationStepActionEmailBase
    extends IAutomationStepActionBase {
    attachment_count?: number;
    email_template_id?: string;
    generic_email?: IVendorEmail;
}
export interface IAutomationStepActionFileBase
    extends IAutomationStepActionEmailBase {
    file_template_id?: string;
}

export interface IAutomationStepActionTask extends IAutomationStepActionBase {
    type: 'task';
    task_title?: string;
}
export interface IAutomationStepActionEmail
    extends IAutomationStepActionEmailBase {
    type: 'email';
}

export interface IAutomationStepActionFlow
    extends IAutomationStepActionFileBase {
    type: 'flow';
}

export interface IAutomationStepActionFile
    extends IAutomationStepActionFileBase {
    type: 'file';
    file_type?: AutomationActionFileType;
}

export interface IAutomationStepActionPipeline
    extends IAutomationStepActionFileBase {
    type: 'pipeline';
}

export type IAutomationStepAction =
    | IAutomationStepActionEmail
    | IAutomationStepActionFile
    | IAutomationStepActionFlow
    | IAutomationStepActionTask
    | IAutomationStepActionPipeline;

export type AutomationStepAction =
    | AutomationStepActionEmail
    | AutomationStepActionFile
    | AutomationStepActionFlow
    | AutomationStepActionTask
    | AutomationStepActionPipeline;

export type TAutomationTriggerUnit = 'minutes' | 'hours' | 'days' | 'weeks';

export type TAutomationTriggerPreposition = 'before' | 'after';

export type TAutomationTriggerType =
    | 'before_project_end_date'
    | 'after_project_end_date'
    | 'before_project_date'
    | 'after_project_date'
    | 'after_workflow_execution'
    | 'after_step_execution'
    | 'after_contract_signed'
    | 'after_questionnaire_submitted'
    | 'after_invoice_paid_in_full'
    | 'after_first_payment_made'
    | 'after_project_move_to_completed'
    | 'after_brochure_submitted'
    | 'after_flow_completed'
    | 'after_all_required_signatures_signed'
    | 'after_session_scheduled'
    | 'before_session_start'
    | 'after_session_start'
    | 'before_session_end'
    | 'after_session_end';

export type TApprovalStatus =
    | 'not_required'
    | 'required'
    | 'pending'
    | 'approved'
    | 'declined';

export type TAutomationActionServerType =
    | 'WorkflowStepFileAction'
    | 'WorkflowStepTaskAction'
    | 'WorkflowStepEmailAction'
    | 'WorkflowStepFlowAction';

export interface IAutomationTriggerUnitOption {
    value: TAutomationTriggerUnit;
    labelPlural: string;
    labelSingular: string;
}

export interface IAutomationTriggerFilters {
    session_config_ids?: string[];
    template_ids?: string[];
    is_all_sessions?: boolean;
    is_all_templates?: boolean;
}

export type IAutomationTriggerFilterTypes = 'session_configs' | 'templates';

export interface IAutomationStepTrigger {
    _id?: string;
    trigger_type: TAutomationTriggerType;
    trigger_date?: string;
    trigger_after_step?: string;
    delay_temporal_unit: TAutomationTriggerUnit;
    delay_temporal_amount: number;
    trigger_filters?: IAutomationTriggerFilters;
}

export interface IAutomationStepSession {
    title: string;
    timezone: TShortTimezone;
    all_day: boolean;
    date_start: string;
    date_time_start: string;
    _id?: string;
}

export enum AutomationBuilderState {
    Create = 'create',
    Preview = 'preview',
    Applied = 'applied',
    Edit = 'edit'
}

export interface IAutomationBuilderParams {
    automationId: string;
    workspace_id?: string;
}

export interface IConfirmModalProps {
    title: string;
    description: string;
    ctaTitle: string;
    ctaOnClick?: () => any | Promise<any>;
    dismissTitle?: string;
    dismissOnClick?: () => any | Promise<any>;
    isVertical?: boolean;
    type?: 'warning' | 'success';
    onCloseHandler?: 'ctaOnClick' | 'dismissOnClick';
    isCTALoader?: boolean;
    isDismissLoader?: boolean;
}

export interface IStepExecuteRequest {
    approval_status: TApprovalStatus;
}

export interface ICustomEmail {
    subject: string;
    body: string;
    general_files: IEmailAttachment[];
    image_files: IEmailAttachment[];
}

export type TAutomationStepExecuteRequest =
    | IStepExecuteRequest
    | (IStepExecuteRequest & ICustomEmail);

export const EMPTY_AUTOMATION_INSTANCE = 'empty_instance';

export const TEMP_AUTOMATION_ID = 'temp_automation_id';

export type TAutomationBlockAction = (props: {
    step: AutomationStep;
    color: string;
}) => JSX.Element;

export type TAutomationEmailEditorState =
    | 'previewFromSidebar'
    | 'previewFromList'
    | 'createNewTemplate'
    | 'nameNewTemplate';

export type TAutomationTriggerEventGroup =
    | 'project'
    | 'file_actions'
    | 'payments'
    | 'scheduler_actions';

export type TAutomationStepBlockErrorMessageKeys =
    | 'missing_session'
    | 'missing_email_template'
    | 'missing_template'
    | 'missing_pipeline_stage'
    | 'missing_task_title';

export interface ICompanyAutomationsRepsonse {
    _id?: string;
    lead_forms_workflow_view_models?: IAutomationLeadFormRelation[];
    workflows: IAutomationPreview[];
}
